import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, shareReplay, switchMap, map, startWith, take, filter } from 'rxjs/operators';
import { Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { UniversalSearchModel, UniversalSearchModelType } from 'src/app/core/models/universal-search.model';
import { DashboardApiService, SignalsApiService } from 'src/app/shared/services/api';
import { GrowthSignalAgentDashboardListDto, GrowthSignalsCombinedSummaryResponseDto, GrowthSignalsMonthlyExportSummaryResponseDto, GrowthSignalsMonthlySummaryResponseDto, SignalCardSummaryDetail } from 'src/app/shared/dto';
import { IsLoading, LoadingWrapper } from 'src/app/shared/decorators';
import { ToastrService } from 'ngx-toastr';
import { ResultTabsetUrlsEnum } from 'src/app/shared/enums';

const LOADER_RUN_AUTOMATION: string = 'run-growth-signals-automation';
const LOADER_AGENTS: string = 'get-growth-signal-agents';
const LOADER_COMBINED_SUMMARY: string = 'get-growth-signals-combined-summary';
const LOADER_REPORT_SUMMARY: string = 'get-growth-signals-report-summary';

@UntilDestroy()
@Component({
  selector: 'app-growth-signals-card-container',
  templateUrl: './growth-signals-card-container.component.html',
  styleUrls: ['../../common-reporting-dashboard.scss'],
  standalone: false
})
export class GrowthSignalsCardContainerComponent implements OnInit {
  @Output() searchTrigger = new EventEmitter<UniversalSearchModelType>();
  @Output() saveSearchTrigger = new EventEmitter();

  // POR-11002 Track-history single-card mode: hide the dropdown + preselect this agent.
  @Input() hideAgentSelector: boolean = false;
  @Input() preselectedAgentId: string | number | null = null;

  @IsLoading(LOADER_RUN_AUTOMATION) isRunAutomation$: Observable<boolean>;
  @IsLoading(LOADER_AGENTS) isGetAgentsLoading$: Observable<boolean>;
  @IsLoading(LOADER_COMBINED_SUMMARY) isGetCombinedSummaryLoading$: Observable<boolean>;
  @IsLoading(LOADER_REPORT_SUMMARY) isGetReportSummaryLoading$: Observable<boolean>;

  selectedAgentSummaryDetail: SignalCardSummaryDetail;
  isShowChart: boolean = true;
  selectedAgent$: Observable<GrowthSignalAgentDashboardListDto | null>;
  summary$: Observable<GrowthSignalsCombinedSummaryResponseDto | GrowthSignalsMonthlySummaryResponseDto | GrowthSignalsMonthlyExportSummaryResponseDto>;

  isLoading$: Observable<boolean>;
  getAgents$: Observable<GrowthSignalAgentDashboardListDto[]>;
  readonly resultTabsetUrlsEnum: typeof ResultTabsetUrlsEnum = ResultTabsetUrlsEnum;
  private selectedAgentSubject = new BehaviorSubject<GrowthSignalAgentDashboardListDto | null>(null);

  constructor(private readonly dashboardApiService: DashboardApiService,
    private readonly signalsApiService: SignalsApiService,
    private readonly toaster: ToastrService) { }

  ngOnInit(): void {
    this.getAgents$ = this.getGrowthSignalAgents().pipe(catchError(() => of([])), shareReplay(1));
    this.selectedAgent$ = this.selectedAgentSubject.asObservable()

    // The preselected agent replaces it and the dropdown is hidden.
    const skipCombinedSummary: boolean = this.hideAgentSelector && this.preselectedAgentId != null;

    this.summary$ = this.selectedAgentSubject.pipe(
      startWith(null),
      filter((agent: GrowthSignalAgentDashboardListDto | null) => !skipCombinedSummary || !!agent),
      switchMap(agent => {
        if (agent) {
          if (agent.email_only) {
            return this.getGrowthSignalsReportSummary(agent.id);
          } else {
            return this.getGrowthSignalsExportSummary(agent.id);
          }
        } else {
          return this.getGrowthSignalsCombinedSummary();
        }
      }),
      shareReplay(1)
    );

    this.isLoading$ = combineLatest([
      this.isGetAgentsLoading$,
      this.isGetCombinedSummaryLoading$,
      this.isGetReportSummaryLoading$,
      this.isRunAutomation$
    ]).pipe(
      map((loaders: boolean[]) => loaders.some((loader: boolean) => loader))
    );

    if (this.preselectedAgentId != null) {
      // POR-11002 defect-2: always select the requested automation, even one not yet in the agents list (never run).
      this.getAgents$.pipe(
        take(1),
        map((agents: GrowthSignalAgentDashboardListDto[]) =>
          agents?.find((a) => String(a?.id) === String(this.preselectedAgentId))
          ?? { id: Number(this.preselectedAgentId), name: '', email_only: false }),
        untilDestroyed(this),
      ).subscribe((agent: GrowthSignalAgentDashboardListDto) => this.updateSelectedAgent(agent));
    }
  }

  updateSelectedAgent(agent: GrowthSignalAgentDashboardListDto | null): void {
    this.selectedAgentSubject.next(agent);
  }

  showReport(): void {
    const selectedAgent = this.selectedAgentSubject.value;
    if (!selectedAgent) {
      return;
    }
    const paramsToUpdate = {
        growth_signals: [selectedAgent.signal_type], 
        saved_search_id: this.selectedAgentSummaryDetail?.saved_search_id
    }
    if (selectedAgent && !this.selectedAgentSummaryDetail?.universal) {
        this.saveSearchTrigger.emit(paramsToUpdate);
    } else {
        const searchModelData: UniversalSearchModelType = {
            ...UniversalSearchModel,
            request_type: 'COMPANIES',
            page: 1,
            verified: 1,
            growth_signals: [selectedAgent.signal_type],
            growth_signal_date: null,
            min_growth_signal_date: null,
            max_growth_signal_date: null,
        };
        this.searchTrigger.emit(searchModelData);
    }
  }

  runAgent(): void {
    const selectedAgent = this.selectedAgentSubject.value;
    const signalId = selectedAgent?.id;
    if (signalId) {
      this.runGrowthAutomation([signalId])
        .pipe(untilDestroyed(this))
        .subscribe({
          next: () => this.toaster.success('Success! Your automation has run.'),
          error: () => this.toaster.error('Failed to run automation. Please try again.')
        });
    }
  }

  onWeeklySummarySet(summaryDetail: SignalCardSummaryDetail): void {
    this.selectedAgentSummaryDetail = summaryDetail;
  }

  @LoadingWrapper(LOADER_AGENTS)
  private getGrowthSignalAgents(): Observable<GrowthSignalAgentDashboardListDto[]> {
    return this.dashboardApiService.getGrowthSignalAgents();
  }

  @LoadingWrapper(LOADER_COMBINED_SUMMARY)
  private getGrowthSignalsCombinedSummary(): Observable<GrowthSignalsCombinedSummaryResponseDto> {
    return this.dashboardApiService.getGrowthSignalsMonthlyCombinedSummary();
  }

  @LoadingWrapper(LOADER_REPORT_SUMMARY)
  private getGrowthSignalsExportSummary(signalId: number): Observable<GrowthSignalsMonthlyExportSummaryResponseDto> {
    return this.dashboardApiService.getGrowthSignalMonthlyExportSummary(signalId);
  }

  @LoadingWrapper(LOADER_REPORT_SUMMARY)
  private getGrowthSignalsReportSummary(agentId: number): Observable<GrowthSignalsMonthlySummaryResponseDto> {
    return this.dashboardApiService.getGrowthSignalsMonthlyReportSummary(agentId);
  }

  @LoadingWrapper(LOADER_RUN_AUTOMATION)
  private runGrowthAutomation(signalIds: Array<number | string>): Observable<unknown> {
    return this.signalsApiService.manualRunSignals({ signal_ids: signalIds });
  }

}